<!-- Copyright (c) 2009-2022. Authors: see NOTICE file.

 Licensed under the Apache License, Version 2.0 (the "License");
 you may not use this file except in compliance with the License.
 You may obtain a copy of the License at

      http://www.apache.org/licenses/LICENSE-2.0

 Unless required by applicable law or agreed to in writing, software
 distributed under the License is distributed on an "AS IS" BASIS,
 WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 See the License for the specific language governing permissions and
 limitations under the License.-->

<template>
<div class="map-container" @click="isActiveImage = true" ref="container">
  <template v-if="!loading && zoom !== null">
    <vl-map
      :data-projection="projectionName"
      :load-tiles-while-animating="true"
      :load-tiles-while-interacting="true"
      :keyboard-event-target="document"
      @pointermove="projectedMousePosition = $event.coordinate"
      @mounted="updateKeyboardInteractions"
      ref="map"
    >

      <vl-view
        :center.sync="center"
        :zoom.sync="zoom"
        :rotation.sync="rotation"
        :max-zoom="maxZoom"
        :max-resolution="Math.pow(2, image.zoom)"
        :extent="extent"
        :projection="projectionName"
        @mounted="viewMounted()"
        ref="view"
      />

      <vl-layer-tile :extent="extent" :preload="1" @mounted="addOverviewMap" ref="baseLayer">
        <vl-source-cytomine
          :projection="projectionName"
          :url="baseLayerURL"
          :tile-load-function="tileLoadFunction"
          :size="imageSize"
          :extent="extent"
          :nb-resolutions="image.zoom"
          ref="baseSource"
          @mounted="setBaseSource()"
          :transition="0"
          :tile-size="[tileSize, tileSize]"
        />
      </vl-layer-tile>

<!--      <vl-layer-image>-->
<!--        <vl-source-raster-->
<!--          v-if="baseSource && colorManipulationOn"-->
<!--          :sources="[baseSource]"-->
<!--          :operation="operation"-->
<!--          :lib="lib"-->
<!--        />-->
<!--      </vl-layer-image>-->

      <annotation-layer
        v-for="layer in selectedLayers"
        :key="'layer-'+layer.id"
        :index="index"
        :layer="layer"
      />

      <select-interaction v-if="activeSelectInteraction" :index="index" />
      <draw-interaction v-if="activeDrawInteraction" :index="index" />
      <modify-interaction v-if="activeModifyInteraction" :index="index" />

    </vl-map>

    <div v-if="configUI['project-tools-main']" class="draw-tools">
      <draw-tools :index="index" @screenshot="takeScreenshot()"/>
    </div>

    <div class="panels">
      <ul>
        <li>
          <a @click="$emit('close')" class="close">
            <i class="fas fa-times-circle"></i>
          </a>
        </li>

        <template v-if="isPanelDisplayed('hide-tools')">
          <li v-if="isPanelDisplayed('info')">
            <a @click="togglePanel('info')" :class="{active: ['info', 'metadata'].includes(activePanel)}">
              <i class="fas fa-info"></i>
            </a>
            <information-panel
              class="panel-options"
              v-show="activePanel === 'info'"
              :index="index"
              @openMetadata="togglePanel('metadata')"
            />
            <div v-show="activePanel === 'metadata'">
              <metadata-panel class="panel-metadata" :index="index" />
            </div>
          </li>

          <li v-if="isPanelDisplayed('digital-zoom')">
            <a @click="togglePanel('digital-zoom')" :class="{active: activePanel === 'digital-zoom'}">
              <i class="fas fa-search"></i>
            </a>
            <digital-zoom class="panel-options" v-show="activePanel === 'digital-zoom'" :index="index"
                          @resetZoom="$refs.view.animate({zoom: image.zoom})"
                          @fitZoom="fitZoom" />
          </li>

          <li v-if="isPanelDisplayed('link') && nbImages > 1">
            <a @click="togglePanel('link')" :class="{active: activePanel === 'link'}">
              <i class="fas fa-link"></i>
            </a>
            <link-panel class="panel-options" v-show="activePanel === 'link'" :index="index" />
          </li>

          <li v-if="isPanelDisplayed('color-manipulation')">
            <a @click="togglePanel('colors')" :class="{active: activePanel === 'colors'}">
              <i class="fas fa-adjust"></i>
            </a>
            <color-manipulation class="panel-options" v-show="activePanel === 'colors'" :index="index" />
          </li>

          <li v-if="isPanelDisplayed('image-layers')">
            <a @click="togglePanel('layers')" :class="{active: activePanel === 'layers'}">
              <i class="fas fa-copy"></i>
            </a>
            <layers-panel class="panel-options" v-show="activePanel === 'layers'"
              :index="index" :layers-to-preload="layersToPreload"
            />
          </li>

          <li v-if="isPanelDisplayed('ontology') && terms && terms.length > 0">
            <a @click="togglePanel('ontology')" :class="{active: activePanel === 'ontology'}">
              <i class="fas fa-hashtag"></i>
            </a>
            <ontology-panel class="panel-options" v-show="activePanel === 'ontology'" :index="index" />
          </li>

          <li  v-if="isPanelDisplayed('property')">
            <a @click="togglePanel('properties')" :class="{active: activePanel === 'properties'}">
              <i class="fas fa-tag"></i>
            </a>
            <properties-panel class="panel-options" v-show="activePanel === 'properties'" :index="index" />
          </li>

          <li v-if="isPanelDisplayed('follow')">
            <a @click="togglePanel('follow')" :class="{active: activePanel === 'follow'}">
              <i class="fas fa-street-view"></i>
            </a>
            <follow-panel class="panel-options" v-show="activePanel === 'follow'" :index="index" :view="$refs.view"/>
          </li>

          <li v-if="isPanelDisplayed('review') && canEdit">
            <a @click="togglePanel('review')" :class="{active: activePanel === 'review'}">
              <i class="fas fa-check-circle"></i>
            </a>
            <review-panel class="panel-options" v-show="activePanel === 'review'" :index="index" />
          </li>
        </template>
      </ul>
    </div>

    <image-controls :index="index" class="image-controls-wrapper" />

    <div class="broadcast" v-if="imageWrapper.tracking.broadcast">
      <i class="fas fa-circle"></i> {{$t('live')}}
    </div>

    <rotation-selector class="rotation-selector-wrapper" :index="index" />

    <!-- Vertical layers-opacity control docked under the rotation compass. Bound to the SAME
         store value as the "Layers opacity" slider of the annotation layers panel, so moving
         either one moves both (vector styles and the raster plane follow the store). Shown while
         at least one annotation layer is selected; hidden while the rotation panel is expanded. -->
    <div v-if="hasSelectedAnnotationLayers" class="layers-opacity-fly"
         v-tooltip="{content: $t('layers-opacity'), placement: 'right'}">
      <input class="lof-slider" type="range" min="0" max="1" step="0.05"
             v-model="layersOpacityCtl" :aria-label="$t('layers-opacity')">
    </div>

    <scale-line v-show="scaleLineCollapsed" :image="image" :zoom="zoom" :mousePosition="projectedMousePosition" />

    <toggle-scale-line :index="index" />

    <annotations-container :index="index" @centerView="centerViewOnAnnot" />

    <!-- what the viewer is still loading: tiles and/or annotations -->
    <loading-indicator-overlay :tilesLoading="tilesLoading" :annotationsLoading="annotationsLoading" />

    <!-- Viewport raster plane: shown only when the annotation renderer is unreachable AND the
         viewport is too dense to load as vectors safely (see decideAnnotationPlane). -->
    <div v-if="annotationsUnavailable" class="annotations-unavailable-notice" role="status">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ $t('annotations-unavailable') }}</span>
    </div>
    <div v-if="tileErrorCount > 0" class="annotations-unavailable-notice" role="status">
      <i class="fas fa-exclamation-triangle"></i>
      <span>{{ $t('tile-load-failed') }} ({{ tileErrorCount }})</span>
    </div>

    <div class="custom-overview" ref="overview">
      <p class="image-name" :class="{hidden: overviewCollapsed}">
        <image-name :image="image" />
      </p>
    </div>
  </template>
</div>
</template>

<script>
import {get} from '@/utils/store-helpers';
import _ from 'lodash';

import ImageName from '@/components/image/ImageName';
import AnnotationLayer from './AnnotationLayer';
import RotationSelector from './RotationSelector';
import ScaleLine from './ScaleLine';
import DrawTools from './DrawTools';
import ImageControls from './ImageControls';
import AnnotationsContainer from './AnnotationsContainer';
import LoadingIndicatorOverlay from './LoadingIndicatorOverlay';

import InformationPanel from './panels/InformationPanel';
import MetadataPanel from './panels/MetadataPanel.vue';
import DigitalZoom from './panels/DigitalZoom';
import ColorManipulation from './panels/ColorManipulation';
import LinkPanel from './panels/LinkPanel';
import LayersPanel from './panels/LayersPanel';
import OntologyPanel from './panels/OntologyPanel';
import PropertiesPanel from './panels/PropertiesPanel';
import FollowPanel from './panels/FollowPanel';
import ReviewPanel from './panels/ReviewPanel';

import SelectInteraction from './interactions/SelectInteraction';
import DrawInteraction from './interactions/DrawInteraction';
import ModifyInteraction from './interactions/ModifyInteraction';
import ToggleScaleLine from './interactions/ToggleScaleLine';

import {addProj, createProj, getProj} from 'vuelayers/lib/ol-ext';

import View from 'ol/View';
import OverviewMap from 'ol/control/OverviewMap';
import {KeyboardPan, KeyboardZoom} from 'ol/interaction';
import {noModifierKeys, targetNotEditable} from 'ol/events/condition';
import WKT from 'ol/format/WKT';

import {Cytomine, ImageConsultation, Annotation, AnnotationType, UserPosition, SliceInstance} from '@/api';
import OlImageLayer from 'ol/layer/Image';
import OlStaticImage from 'ol/source/ImageStatic';
import {updateTermProperties, updateTrackProperties, updateAnnotationLinkProperties} from '@/utils/annotation-utils';
import {updateToken} from '@/utils/token-utils';

// import {constLib, operation} from '@/utils/color-manipulation.js';

import constants from '@/utils/constants.js';

// Viewport raster plane: above this many annotations in view the viewer will NOT fall back to
// loading vectors when the renderer is unreachable (matches the renderer's own VECTOR_MAX).
const ANNOTATION_FALLBACK_MAX = 2000;

// PIMS tile encoding since 2026-10-05: JPEG quality 95, chroma subsampling off.
const TILE_ENCODING_VERSION = 'q95-444';

export default {
  name: 'cytomine-image',
  props: {
    index: String
  },
  components: {
    ImageName,

    AnnotationLayer,

    RotationSelector,
    ScaleLine,
    DrawTools,
    ImageControls,
    AnnotationsContainer,
    LoadingIndicatorOverlay,

    InformationPanel,
    MetadataPanel,
    DigitalZoom,
    ColorManipulation,
    LinkPanel,
    LayersPanel,
    OntologyPanel,
    PropertiesPanel,
    FollowPanel,
    ReviewPanel,

    SelectInteraction,
    DrawInteraction,
    ModifyInteraction,
    ToggleScaleLine
  },
  data() {
    return {
      minZoom: 0,

      projectedMousePosition: [0, 0],

      baseSource: null,
      routedAnnotation: null,
      selectedAnnotation: null,

      timeoutSavePosition: null,

      loading: true,

      overview: null,

      format: new WKT(),

      // viewport raster plane: renderer unreachable over a dense viewport
      annotationsUnavailable: false,
      // tiles that failed twice in a row (cleared after a quiet period)
      tileErrorCount: 0,
      tileErrorTimer: null,
      // loading indicator: counters of in-flight tile / annotation requests
      tileLoadingCount: 0,
      annotationLoadingCount: 0,
      tilesLoading: false,
      annotationsLoading: false,
      tilesHideTimer: null,
      annotsHideTimer: null,
    };
  },
  computed: {
    shortTermToken: get('currentUser/shortTermToken'),
    document() {
      return document;
    },
    project: get('currentProject/project'),
    routedAction() {
      return this.$route.query.action;
    },
    configUI: get('currentProject/configUI'),
    viewerModule() {
      return this.$store.getters['currentProject/currentViewerModule'];
    },
    imageModule() {
      return this.$store.getters['currentProject/imageModule'](this.index);
    },
    viewerWrapper() {
      return this.$store.getters['currentProject/currentViewer'];
    },
    nbImages() {
      return Object.keys(this.viewerWrapper.images).length;
    },
    imageWrapper() {
      return this.viewerWrapper.images[this.index];
    },
    image() {
      return this.imageWrapper.imageInstance;
    },
    slices() {
      return this.imageWrapper.activeSlices;
    },
    sliceIds() {
      return this.slices.map(slice => slice.id);
    },
    canEdit() {
      return this.$store.getters['currentProject/canEditImage'](this.image);
    },
    projectionName() {
      return `CYTO-${this.image.id}`;
    },
    terms() {
      return this.$store.getters['currentProject/terms'];
    },
    selectedLayers() {
      return this.imageWrapper.layers.selectedLayers || [];
    },

    // ---- viewport raster plane ----
    // The raster is ONE picture for every visible layer; an annotation layer IS a user, and the
    // renderer filters on `users`. null = nothing visible = draw no raster at all.
    annotationVisibleUserIds() {
      const sel = this.selectedLayers || [];
      const ids = sel.filter(l => l.visible !== false).map(l => l.id);
      return ids.length ? ids : null;
    },
    annotationVisibleUserKey() {
      return (this.annotationVisibleUserIds || []).slice().sort().join(',');
    },
    // Term visibility is applied server-side (`terms=`), so both raster regimes honour it.
    annotationTermFilterKey() {
      const style = this.imageWrapper.style || {};
      const hidden = (style.terms || []).filter(t => t.visible === false).map(t => t.id).sort();
      return `${hidden.join(',')}|${style.displayNoTerm === false ? 'noTermHidden' : ''}`;
    },
    hasSelectedAnnotationLayers() {
      const layers = this.imageWrapper.layers && this.imageWrapper.layers.selectedLayers;
      return Array.isArray(layers) && layers.length > 0;
    },
    // One source of truth: the same imageWrapper.style.layersOpacity the layers panel slider
    // binds to, so the vertical control and the panel slider stay in sync from either side.
    layersOpacityCtl: {
      get() {
        return this.imageWrapper.style.layersOpacity;
      },
      set(value) {
        this.$store.commit(this.imageModule + 'setLayersOpacity', Number(value));
      }
    },
    // The annotation-opacity slider must drive the raster too; applied client-side (free).
    annotationRasterOpacity() {
      const v = this.imageWrapper.style && this.imageWrapper.style.layersOpacity;
      return typeof v === 'number' ? v : 1;
    },
    isActiveImage: {
      get() {
        return this.viewerWrapper.activeImage === this.index;
      },
      set(value) {
        if (value) {
          if (this.viewerWrapper) {
            this.$store.commit(this.viewerModule + 'setActiveImage', this.index);
          }
        } else {
          throw new Error('Cannot unset active map');
        }
      }
    },
    activePanel() {
      return this.imageWrapper.activePanel;
    },
    activeTool() {
      return this.imageWrapper.draw.activeTool;
    },
    activeEditTool() {
      return this.imageWrapper.draw.activeEditTool;
    },
    maxZoom() {
      return this.$store.getters[this.imageModule + 'maxZoom'];
    },

    center: {
      get() {
        return this.imageWrapper.view.center;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setCenter', {index: this.index, center: value});
      }
    },
    zoom: {
      get() {
        return this.imageWrapper.view.zoom;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setZoom', {index: this.index, zoom: Number(value)});
      }
    },
    rotation: {
      get() {
        return this.imageWrapper.view.rotation;
      },
      set(value) {
        this.$store.dispatch(this.viewerModule + 'setRotation', {index: this.index, rotation: Number(value)});
      }
    },

    viewState() {
      return {center: this.center, zoom: this.zoom, rotation: this.rotation};
    },

    extent() {
      return [0, 0, this.image.width, this.image.height];
    },
    imageSize() {
      return [this.image.width, this.image.height];
    },
    tileSize() {
      return this.image.tileSize;
    },
    baseLayerProcessingParams() {
      return this.$store.getters[this.imageModule + 'tileRequestParams'];
    },
    baseLayerSliceParams() {
      return {
        zSlices: this.slices[0].zStack,
        timepoints: this.slices[0].time
      };
    },
    baseLayerURLQuery() {
      let query = new URLSearchParams({...this.baseLayerSliceParams, ...this.baseLayerProcessingParams}).toString();
      if (query.length > 0) {
        return `?${query}`;
      }
      return query;
    },
    baseLayerURL() {
      let slice = this.slices[0];
      // tileEncoding names how PIMS encodes tiles (PIMS_JPEG_QUALITY / PIMS_JPEG_SUBSAMPLE_MODE).
      // Browsers cache tiles for 24 h by URL, so changing this value makes every browser fetch the
      // new tiles at once. Change it whenever the PIMS tile encoding changes. Core, PIMS and the
      // annotation renderer ignore the parameter. See PIMS_QUALITY.md.
      let query = this.baseLayerURLQuery;
      query += `${query ? '&' : '?'}tileEncoding=${TILE_ENCODING_VERSION}`;
      return Cytomine.instance.host + Cytomine.instance.basePath + `sliceinstance/${slice.id}/normalized-tile/zoom/{z}/tx/{x}/ty/{y}.jpg${query}`;
    },
    tileLoadFunction() {
      return (tile, src) => {
        // "Tiles loading" is accounted HERE, not from OpenLayers' tile events: OL disposes tiles
        // that leave the view mid-flight without firing tileloadend/tileloaderror, so an
        // event-driven counter can only leak upward. This loader owns every terminal path and
        // guarantees exactly one decrement per invocation.
        this.onTileLoadStart();
        let ended = false;
        const endOnce = () => {
          if (!ended) {
            ended = true;
            this.onTileLoadEnd();
          }
        };
        const xhr = new XMLHttpRequest();
        xhr.responseType = 'blob';
        xhr.timeout = 30000; // a hung connection must become a terminal event, never a stuck tile
        xhr.open('GET', src);
        xhr.setRequestHeader('Authorization', 'Bearer ' + this.shortTermToken);
        // Every failure path must END the tile: OpenLayers' tile queue has a fixed number of
        // slots, so tiles that never resolve stall the whole map. Retry once with a small jitter,
        // then complete the tile as transparent and tell the user: a silently blank patch of a
        // diagnostic slide reads as "no tissue here", which must not happen.
        const BLANK = 'data:image/gif;base64,R0lGODlhAQABAIAAAAAAAP///yH5BAEAAAAALAAAAAABAAEAAAIBRAA7';
        const settle = (reason) => {
          endOnce();
          if (!tile.__retried) {
            tile.__retried = true;
            setTimeout(() => {
              if (this._isDestroyed || this._isBeingDestroyed) {
                return;
              }
              this.tileLoadFunction()(tile, src);
            }, 400 + Math.random() * 400);
            return;
          }
          try {
            tile.getImage().src = BLANK;
          } catch (e) {
            // tile disposed meanwhile: nothing left to draw on
          }
          this.noteTileFailure(reason);
        };
        xhr.addEventListener('load', () => {
          try {
            if (xhr.status < 200 || xhr.status >= 300) {
              settle('http ' + xhr.status);
              return;
            }
            const tileImage = tile.getImage();
            if (!(tileImage instanceof HTMLImageElement)) {
              endOnce(); // disposed tiles come back as a blank canvas that never fires 'load'
              return;
            }
            const url = URL.createObjectURL(xhr.response);
            tileImage.addEventListener('load', () => {
              URL.revokeObjectURL(url);
              tile.__retried = false; // a healthy tile earns back its retry
              endOnce();
            });
            tileImage.addEventListener('error', () => {
              URL.revokeObjectURL(url);
              settle('decode');
            });
            tileImage.src = url;
          } catch (e) {
            endOnce(); // tile disposed under us (pan/zoom mid-flight)
          }
        });
        xhr.addEventListener('error', () => settle('network'));
        xhr.addEventListener('timeout', () => settle('timeout'));
        xhr.addEventListener('abort', () => settle('abort'));
        xhr.send();
      };
    },

    layersToPreload() {
      let layers = [];
      let annot = this.selectedAnnotation || this.routedAnnotation;
      if (annot) {
        layers.push(annot.type === AnnotationType.REVIEWED ? -1 : (annot.username));
      }
      if (this.routedAction === 'review' && !layers.includes(-1)) {
        layers.push(-1);
      }
      return layers;
    },

    overviewCollapsed() {
      return this.overview ? this.overview.getCollapsed() : this.imageWrapper.view.overviewCollapsed;
    },
    scaleLineCollapsed() {
      return !this.imageWrapper.view.scaleLineCollapsed;
    },

    correction() {
      return ['correct-add', 'correct-remove'].includes(this.activeEditTool);
    },
    activeSelectInteraction() {
      return this.activeTool === 'select';
    },
    activeDrawInteraction() {
      return !this.activeSelectInteraction || this.correction;
    },
    activeModifyInteraction() {
      return this.activeSelectInteraction && this.activeEditTool && !this.correction;
    },
    idealZoom() {
      let container = this.$refs.container;
      let idealZoom = this.maxZoom;
      let factor = this.maxZoom - this.image.zoom;
      let mapWidth = this.image.width * Math.pow(2, factor);
      let mapHeight = this.image.height * Math.pow(2, factor);
      while (mapWidth > container.clientWidth || mapHeight > container.clientHeight) {
        mapWidth /= 2;
        mapHeight /= 2;
        idealZoom --;
      }
      return idealZoom;
    }
  },
  watch: {
    viewState() {
      this.savePosition();
    },
    // A layer toggle changes which annotations exist in the picture: re-decide (and re-render).
    annotationVisibleUserKey() {
      this.decideAnnotationPlane();
    },
    // A term toggle only matters while a picture is shown; the vector plane styles terms itself.
    annotationTermFilterKey() {
      if (this._annRasterLayer) {
        this.decideAnnotationPlane();
      }
    },
    annotationRasterOpacity(v) {
      if (this._annRasterLayer) {
        this._annRasterLayer.setOpacity(v);
      }
    },
    overviewCollapsed(value) {
      this.$store.commit(this.imageModule + 'setOverviewCollapsed', value);
    }
  },
  methods: {
    setInitialZoom() {
      if (this.zoom !== null) {
        return; // not the first time the viewer is opened => zoom was already initialized
      }
      this.zoom = this.idealZoom;
    },

    async updateMapSize() {
      await this.$nextTick();
      if (this.$refs.map) {
        this.$refs.map.updateSize();
      }
    },

    async updateKeyboardInteractions() {
      await this.$refs.map.$createPromise; // wait for ol.Map to be created

      this.$refs.map.$map.getInteractions().forEach(interaction => {
        if (interaction instanceof KeyboardPan || interaction instanceof KeyboardZoom) {
          interaction.condition_ = (mapBrowserEvent) => {
            return noModifierKeys(mapBrowserEvent)
              && targetNotEditable(mapBrowserEvent)
              && this.isActiveImage
              && !mapBrowserEvent.originalEvent.target.classList.contains('ql-editor');
          };
        }
      });

      // Viewport raster plane: decide raster-vs-vector once per view change (moveend always
      // fires, unlike the vector loader once the raster is in force) and let a click on the
      // picture select a real annotation. When the feature is switched off nothing is bound and
      // the viewer behaves exactly as before.
      const map = this.$refs.map.$map;
      if (constants.ANNOTATION_RENDERER_ENABLED && map && !this._annPlaneBound) {
        this._annPlaneBound = true;
        // Mark the plane undecided as soon as the view starts changing, so the vector loader does
        // not fetch a viewport that is about to become a picture (measured: a 4.4 MB load fired
        // during one zoom-out before the decision landed). The layer resumes any load it skipped,
        // incrementally, once the decision says 'vector'. View events rather than movestart alone:
        // programmatic zooms do not fire movestart.
        const pending = () => this.$eventBus.$emit('annotationPlane', {index: this.index, plane: 'pending'});
        map.getView().on('change:resolution', pending);
        map.getView().on('change:center', pending);
        map.on('movestart', pending);
        map.on('moveend', () => this.decideAnnotationPlane());
        map.on('singleclick', evt => this.pickFromRaster(evt));
        this.decideAnnotationPlane();
      }
    },

    onTileLoadStart() {
      this.tileLoadingCount++;
      clearTimeout(this.tilesHideTimer);
      this.tilesLoading = true;
    },
    onTileLoadEnd() {
      this.tileLoadingCount = Math.max(0, this.tileLoadingCount - 1);
      if (this.tileLoadingCount === 0) {
        clearTimeout(this.tilesHideTimer);
        this.tilesHideTimer = setTimeout(() => {
          this.tilesLoading = false;
        }, 250); // let a burst of tiles settle without flicker
      }
    },
    onAnnotationLoadingStart(payload) {
      if (payload && payload.index !== undefined && payload.index !== this.index) {
        return;
      }
      this.annotationLoadingCount++;
      clearTimeout(this.annotsHideTimer);
      this.annotationsLoading = true;
    },
    onAnnotationLoadingEnd(payload) {
      if (payload && payload.index !== undefined && payload.index !== this.index) {
        return;
      }
      this.annotationLoadingCount = Math.max(0, this.annotationLoadingCount - 1);
      if (this.annotationLoadingCount === 0) {
        clearTimeout(this.annotsHideTimer);
        this.annotsHideTimer = setTimeout(() => {
          this.annotationsLoading = false;
        }, 250);
      }
    },

    // Tiles fail in bursts (a lost connection kills every tile in flight): show one notice, and
    // clear it only after a quiet period so it does not flicker once per tile.
    noteTileFailure(reason) {
      this.tileErrorCount++;
      console.warn('tile load failed (' + reason + ')');
      clearTimeout(this.tileErrorTimer);
      this.tileErrorTimer = setTimeout(() => {
        this.tileErrorCount = 0;
      }, 8000);
    },

    // ---- viewport annotation raster ----------------------------------------------------------
    // Above a budget of annotations in view, the renderer draws them as one picture plus an
    // id/bbox sidecar and a hidden index plane (pixel -> feature); below it, it answers 204 and
    // the vector layer runs exactly as before. Additive: one image layer on top, removed the
    // moment vectors take over. Nothing here writes; coordinates are never modified.

    rendererUrl(path) {
      return `${window.location.origin}${constants.ANNOTATION_RENDERER_PATH}/v1${path}`;
    },

    // Every raw fetch outside axios gets its token here. The axios interceptor refreshes the
    // Keycloak token on axios calls only, so a viewer whose hot path is raw fetch could otherwise
    // outlive its token. updateToken() refreshes only when < 70 s of validity remain; a 4 s cap
    // makes a hung refresh degrade to "use the current token" instead of freezing the viewer.
    async authToken() {
      try {
        const token = await Promise.race([
          updateToken(),
          new Promise(resolve => setTimeout(() => resolve(null), 4000))
        ]);
        return token || this.shortTermToken;
      } catch (e) {
        return this.shortTermToken;
      }
    },

    // Terms hidden in the ontology panel, expressed as the renderer's `terms=` filter.
    // null = no filter. Hiding "no term" cannot be expressed on its own, so it becomes "only
    // annotations carrying a visible term"; an empty visible set is sent as an impossible id.
    visibleTermIds() {
      const style = this.imageWrapper.style || {};
      const terms = style.terms || [];
      const anyHidden = terms.some(t => t.visible === false);
      if (!anyHidden && style.displayNoTerm !== false) {
        return null;
      }
      const visible = terms.filter(t => t.visible !== false).map(t => t.id);
      return visible.length ? visible : [-1];
    },

    // The request describing the current viewport, or null when the map is not ready.
    currentViewportRequest() {
      const map = this.$refs.map && this.$refs.map.$map;
      if (!map || !this.image) {
        return null;
      }
      const size = map.getSize();
      if (!size || !size[0] || !size[1]) {
        return null;
      }
      const w = Math.min(2048, Math.max(16, Math.round(size[0])));
      const h = Math.min(2048, Math.max(16, Math.round(size[1])));
      const ext = map.getView().calculateExtent(size);
      const bbox = ext.map(v => Math.round(v)).join(',');
      const users = this.annotationVisibleUserIds;
      if (!users) {
        return {ext, users: null};
      }
      let query = `?bbox=${bbox}&width=${w}&height=${h}&users=${users.join(',')}`;
      const terms = this.visibleTermIds();
      if (terms) {
        query += `&terms=${terms.join(',')}`;
      }
      return {ext, w, h, users, query, key: `${this.image.id}|${query}`};
    },

    // Headers-only probe (~5 ms, ~160 bytes): total, plane and change fingerprint for a request.
    async fetchRenderHeaders(query) {
      const r = await fetch(this.rendererUrl(`/annotation-render/${this.image.id}`) + query + '&probe=1',
        {headers: {Authorization: 'Bearer ' + await this.authToken()}});
      if (!r.ok && r.status !== 204) {
        return null;
      }
      return {
        fp: r.headers.get('X-Annotation-Fingerprint'),
        total: Number(r.headers.get('X-Annotation-Total') || 0)
      };
    },

    // Small LRU of already-rendered viewports: zooming back to a seen view costs nothing. It is
    // the reader's own memory (zero server cost) and each entry is verified by a probe on reuse.
    rememberRaster(key, entry) {
      if (!this._annCache) {
        this._annCache = new Map();
      }
      this._annCache.set(key, entry);
      while (this._annCache.size > 12) {
        this._annCache.delete(this._annCache.keys().next().value);
      }
    },

    async confirmCachedRaster(key, req) {
      try {
        const h = await this.fetchRenderHeaders(req.query);
        const hit = this._annCache && this._annCache.get(key);
        if (h && hit && h.fp && hit.fingerprint && h.fp !== hit.fingerprint) {
          this._annCache.delete(key);
          this.decideAnnotationPlane();
        }
      } catch (e) {
        // a failed confirmation must never disturb what is already on screen
      }
    },

    // Decide raster-vs-vector for the current viewport. One request does both: 204 means "the
    // vector path is strictly better here" (today's code runs), anything else IS the picture.
    async decideAnnotationPlane() {
      if (!constants.ANNOTATION_RENDERER_ENABLED) {
        return;
      }
      if (this._annPlanePending) {
        this._annPlaneAgain = true;
        return;
      }
      this._annPlanePending = true;
      try {
        const req = this.currentViewportRequest();
        if (!req) {
          return;
        }
        if (!req.users) {
          // every annotation layer hidden or none loaded: nothing to draw, no request
          this.annotationsUnavailable = false;
          this.$eventBus.$emit('annotationPlane', {index: this.index, plane: 'vector', extent: req.ext, buf: null});
          return;
        }
        const hit = this._annCache && this._annCache.get(req.key);
        if (hit) {
          this._annCache.delete(req.key);
          this._annCache.set(req.key, hit); // refresh LRU position
          this.annotationsUnavailable = false;
          this._annFingerprint = hit.fingerprint;
          this.$eventBus.$emit('annotationPlane', {index: this.index, 
            plane: hit.plane, extent: req.ext, req, buf: hit.buf ? hit.buf.slice(0) : null
          });
          this.confirmCachedRaster(req.key, req); // show it now, verify it after
          return;
        }

        // Last-issued-wins: a stale response (older extent) must never record its fingerprint.
        const seq = this._annFpSeq = (this._annFpSeq || 0) + 1;
        this.onAnnotationLoadingStart();
        let r;
        try {
          r = await fetch(this.rendererUrl(`/annotation-render/${this.image.id}`) + req.query,
            {headers: {Authorization: 'Bearer ' + await this.authToken()}});
        } finally {
          this.onAnnotationLoadingEnd();
        }
        // 204 is the renderer DECIDING that vectors are cheaper: a healthy answer. Any other
        // non-ok status means it could not answer at all (fetch does not throw on 502).
        if (!r.ok && r.status !== 204) {
          throw new Error(`annotation renderer unavailable (HTTP ${r.status})`);
        }
        const raster = r.status !== 204;
        this.annotationsUnavailable = false;
        const respFp = r.headers.get('X-Annotation-Fingerprint');
        if (seq === this._annFpSeq) {
          this._annFingerprint = respFp;
        }
        // Remembered so that, if the renderer later becomes unreachable, we know whether falling
        // back to vectors here would be reasonable or ruinous.
        const known = Number(r.headers.get('X-Annotation-Total'));
        if (Number.isFinite(known)) {
          this._annLastTotal = known;
        }
        const body = raster ? await r.arrayBuffer() : null;
        this.rememberRaster(req.key, {
          plane: raster ? 'raster' : 'vector', buf: body ? body.slice(0) : null,
          total: known, fingerprint: respFp
        });
        this.$eventBus.$emit('annotationPlane', {index: this.index, 
          plane: raster ? 'raster' : 'vector', extent: req.ext, req, buf: body
        });
      } catch (e) {
        // Renderer unreachable. Falling back to vectors is today's behaviour and right for a
        // viewport of ordinary size, but unbounded on a dense one (the load that can take Core
        // down, at the worst moment). Fall back when it is reasonable, otherwise say plainly that
        // annotations are unavailable rather than show an empty layer.
        const last = this._annLastTotal;
        if (Number.isFinite(last) && last > ANNOTATION_FALLBACK_MAX) {
          this.annotationsUnavailable = true;
          this.$eventBus.$emit('annotationPlane', {index: this.index, plane: 'unavailable', extent: null});
        } else {
          this.annotationsUnavailable = false;
          this.$eventBus.$emit('annotationPlane', {index: this.index, plane: 'vector', extent: null});
        }
      } finally {
        this._annPlanePending = false;
        if (this._annPlaneAgain) {
          this._annPlaneAgain = false;
          this.decideAnnotationPlane();
        }
      }
    },

    onAnnotationPlane({index, plane, extent, req, buf}) {
      if (index !== this.index || plane === 'pending') {
        return; // another viewer's slide, or not a decision
      }
      if (plane === 'raster') {
        this.drawAnnotationRaster(extent, req, buf);
      } else {
        this.clearAnnotationRaster();
      }
    },

    // A create/edit/delete makes every remembered viewport of this image stale. On the raster
    // plane redraw, debounced so a burst of edits costs one request; the writer's own shape is
    // already on screen as a vector feature.
    onAnnotationWrite(annot) {
      if (annot && annot.image && annot.image !== this.image.id) {
        return; // a write on another open slide
      }
      if (this._annCache) {
        this._annCache.clear();
      }
      if (!this._annRasterLayer || !this._annLastReq) {
        return;
      }
      clearTimeout(this._annWriteTimer);
      this._annWriteTimer = setTimeout(() => {
        if (this._annRasterLayer && this._annLastReq) {
          this.drawAnnotationRaster(this._annLastReq.ext, this._annLastReq);
        }
      }, 250);
    },

    // The viewer's 10 s timer (CytomineViewer emits reloadAnnotations with no payload) is how a
    // second viewer sees another user's write. It used to refetch the whole viewport every tick,
    // on every layer (measured: 1.2 MB per tick at an ordinary editing zoom). Now the tick costs
    // one headers-only probe (~160 bytes) and only a moved fingerprint triggers work: a re-render
    // on the raster plane, the usual layer reload on the vector plane. Explicit reloads (a
    // payload: own write, slice change, hard reload) pass straight through to the layers as
    // before, and if the renderer cannot be asked the tick falls back to the old full reload.
    async onReloadAnnotations(payload) {
      if (payload || !constants.ANNOTATION_RENDERER_ENABLED) {
        return; // explicit reload or feature off: AnnotationLayer handles it exactly as before
      }
      const req = this._annLastReq || this.currentViewportRequest();
      if (!req || !req.users || !req.query) {
        return;
      }
      let changed = true;
      try {
        const h = await this.fetchRenderHeaders(req.query);
        if (h && h.fp) {
          changed = !this._annFingerprint || h.fp !== this._annFingerprint;
          if (h.fp !== this._annFingerprint && this._annCache) {
            this._annCache.delete(req.key);
          }
        }
      } catch (e) {
        changed = true; // renderer unreachable: behave as today (full reload)
      }
      if (!changed) {
        return;
      }
      if (this._annRasterLayer) {
        this.decideAnnotationPlane();
      } else {
        this.$eventBus.$emit('reloadAnnotations', {idImage: this.image.id, fromProbe: true});
      }
    },

    async drawAnnotationRaster(extent, req, prefetched) {
      const map = this.$refs.map && this.$refs.map.$map;
      if (!map || !this.image || !extent || !req) {
        return;
      }
      try {
        let buf = prefetched;
        if (!buf) {
          const seq = this._annFpSeq = (this._annFpSeq || 0) + 1;
          this.onAnnotationLoadingStart();
          let resp;
          try {
            resp = await fetch(this.rendererUrl(`/annotation-render/${this.image.id}`) + req.query,
              {headers: {Authorization: 'Bearer ' + await this.authToken()}});
          } finally {
            this.onAnnotationLoadingEnd();
          }
          if (resp.status === 204) {
            // the viewport dropped under the budget (e.g. deletions): back to vectors
            this.$eventBus.$emit('annotationPlane', {index: this.index, plane: 'vector', extent, req, buf: null});
            return;
          }
          if (!resp.ok) {
            this.clearAnnotationRaster();
            return;
          }
          const fp = resp.headers.get('X-Annotation-Fingerprint');
          if (fp && seq === this._annFpSeq) {
            this._annFingerprint = fp;
          }
          buf = await resp.arrayBuffer();
          this.rememberRaster(req.key, {
            plane: 'raster', buf: buf.slice(0),
            total: Number(resp.headers.get('X-Annotation-Total') || 0), fingerprint: fp
          });
        }

        // One body, three parts: picture, id/bbox sidecar, index plane.
        const dv = new DataView(buf);
        const pngLen = dv.getUint32(0, true);
        const sideLen = dv.getUint32(4, true);
        const idxLen = dv.getUint32(8, true);
        const HDR = 12;
        const png = new Blob([new Uint8Array(buf, HDR, pngLen)], {type: 'image/png'});

        const side = [];
        for (let o = HDR + pngLen; o + 14 <= HDR + pngLen + sideLen; o += 14) {
          side.push({
            id: dv.getUint32(o, true),
            x0: dv.getUint16(o + 4, true), y0: dv.getUint16(o + 6, true),
            x1: dv.getUint16(o + 8, true), y1: dv.getUint16(o + 10, true),
            termId: dv.getUint16(o + 12, true)
          });
        }
        this._annSidecar = side;

        // Decode the index plane once per viewport; after this a click is one array lookup.
        this._annIndex = null;
        if (idxLen > 0) {
          try {
            const bytes = new Uint8Array(buf, HDR + pngLen + sideLen, idxLen);
            const bmp = await createImageBitmap(new Blob([bytes], {type: 'image/png'}));
            const cv = document.createElement('canvas');
            cv.width = bmp.width;
            cv.height = bmp.height;
            const ctx = cv.getContext('2d', {willReadFrequently: true});
            ctx.drawImage(bmp, 0, 0);
            const px = ctx.getImageData(0, 0, bmp.width, bmp.height).data;
            const codes = new Uint16Array(bmp.width * bmp.height);
            for (let i = 0, j = 0; i < codes.length; i++, j += 4) {
              codes[i] = (px[j] << 8) | px[j + 1];
            }
            // indexed by map coordinates, not viewport pixels: the view may have moved since
            this._annIndex = {w: bmp.width, h: bmp.height, codes, ext: extent.slice(0, 4)};
            bmp.close();
          } catch (e) {
            this._annIndex = null;
          }
        }

        const url = URL.createObjectURL(png);
        const source = new OlStaticImage({
          url, imageExtent: extent.slice(0, 4), projection: map.getView().getProjection()
        });
        if (!this._annRasterLayer) {
          this._annRasterLayer = new OlImageLayer({source, zIndex: 40});
          map.addLayer(this._annRasterLayer);
        } else {
          this._annRasterLayer.setSource(source);
        }
        this._annRasterLayer.setOpacity(this.annotationRasterOpacity);
        if (this._annBlobUrl) {
          URL.revokeObjectURL(this._annBlobUrl);
        }
        this._annBlobUrl = url;
        this._annLastReq = req;
      } catch (e) {
        this.clearAnnotationRaster();
      }
    },

    clearAnnotationRaster() {
      const map = this.$refs.map && this.$refs.map.$map;
      if (this._annRasterLayer && map) {
        map.removeLayer(this._annRasterLayer);
      }
      this._annRasterLayer = null;
      this._annSidecar = [];
      this._annIndex = null;
      this._annLastReq = null;
      if (this._annBlobUrl) {
        URL.revokeObjectURL(this._annBlobUrl);
        this._annBlobUrl = null;
      }
    },

    // pixel -> annotation, from the index plane (exact where features overlap, no round trip).
    annotationAtCoordinate(coordinate) {
      const ix = this._annIndex;
      const side = this._annSidecar || [];
      if (!ix || !ix.ext || !side.length) {
        return null;
      }
      const e = ix.ext;
      const cx = Math.round((coordinate[0] - e[0]) / (e[2] - e[0]) * ix.w);
      const cy = Math.round((e[3] - coordinate[1]) / (e[3] - e[1]) * ix.h);
      const at = (qx, qy) => (qx < 0 || qy < 0 || qx >= ix.w || qy >= ix.h) ? 0 : ix.codes[qy * ix.w + qx];
      let code = at(cx, cy);
      if (!code) {
        // a few pixels of tolerance around the cursor, majority vote
        const tally = new Map();
        for (let dy = -2; dy <= 2; dy++) {
          for (let dx = -2; dx <= 2; dx++) {
            const c = at(cx + dx, cy + dy);
            if (c) {
              tally.set(c, (tally.get(c) || 0) + 1);
            }
          }
        }
        let bestN = 0;
        tally.forEach((n, c) => {
          if (n > bestN) {
            bestN = n;
            code = c;
          }
        });
      }
      return (code > 0 && code <= side.length) ? side[code - 1] : null;
    },

    // True when two DIFFERENT features meet inside the clicked neighbourhood: the click landed on
    // a boundary, and the server (which tests real geometry) should decide.
    pickIsAmbiguous(coordinate) {
      const ix = this._annIndex;
      if (!ix || !ix.ext) {
        return false;
      }
      const e = ix.ext;
      const cx = Math.round((coordinate[0] - e[0]) / (e[2] - e[0]) * ix.w);
      const cy = Math.round((e[3] - coordinate[1]) / (e[3] - e[1]) * ix.h);
      let seen = 0;
      for (let dy = -1; dy <= 1; dy++) {
        for (let dx = -1; dx <= 1; dx++) {
          const qx = cx + dx;
          const qy = cy + dy;
          if (qx < 0 || qy < 0 || qx >= ix.w || qy >= ix.h) {
            continue;
          }
          const c = ix.codes[qy * ix.w + qx];
          if (c) {
            if (seen && c !== seen) {
              return true;
            }
            seen = c;
          }
        }
      }
      return false;
    },

    // Metadata plus a viewport-simplified outline for one annotation, from the renderer's pick
    // endpoint (a click no longer downloads the full geometry). `query` is `id=<id>&x=0&y=0` or
    // `x=<x>&y=<y>&n=1&users=...`. The caller marks whatever it builds from it display-only.
    async pickAnnotationSummary(query) {
      const tol = Math.max(0, this.$refs.map.$map.getView().getResolution() || 0);
      const url = this.rendererUrl(`/annotation-pick/${this.image.id}`) + `?${query}&geom=simplified&tol=${tol.toFixed(3)}`;
      const r = await fetch(url, {headers: {Authorization: 'Bearer ' + await this.authToken()}});
      if (!r.ok) {
        return null;
      }
      const d = await r.json();
      return (d.annotations && d.annotations.length && d.annotations[0].location) ? d.annotations[0] : null;
    },

    // A click on the picture must select a real annotation. The index plane answers locally;
    // otherwise the server resolves the point. Either way this ends in the viewer's ordinary
    // selection path, so details, editing and undo behave exactly as for a vector feature.
    async pickFromRaster(evt) {
      if (!this._annRasterLayer || !this.image || !this.activeSelectInteraction) {
        return; // vector plane, or a drawing tool owns the click
      }
      try {
        let id = null;
        const local = this.annotationAtCoordinate(evt.coordinate);
        if (local && !this.pickIsAmbiguous(evt.coordinate)) {
          id = local.id;
        } else if (!local && this._annIndex) {
          // the index plane is authoritative: nothing there means the reader clicked background
          this.$store.commit(this.imageModule + 'clearSelectedFeatures');
          return;
        }
        const users = this.annotationVisibleUserIds || [];
        const a = await this.pickAnnotationSummary(id !== null
          ? `id=${id}&x=0&y=0`
          : `x=${Math.round(evt.coordinate[0])}&y=${Math.round(evt.coordinate[1])}&n=1&users=${users.join(',')}`);
        if (!a) {
          return;
        }
        if (id === null && !a.hit && a.centroid) {
          // aggregate regime (no index plane): the server answered with the NEAREST annotation.
          // Accept it only when it is close on screen, otherwise the click was on background.
          const res = this.$refs.map.$map.getView().getResolution() || 1;
          const d = Math.hypot(a.centroid.x - evt.coordinate[0], a.centroid.y - evt.coordinate[1]) / res;
          if (d > 12) {
            this.$store.commit(this.imageModule + 'clearSelectedFeatures');
            return;
          }
        }
        const annot = new Annotation({
          id: a.id,
          class: a.reviewed ? 'be.cytomine.domain.ontology.ReviewedAnnotation' : 'be.cytomine.domain.ontology.UserAnnotation',
          project: a.project,
          image: this.image.id,
          slice: a.slice,
          user: a.user,
          location: a.location,
          created: a.created,
          centroid: a.centroid,
          area: a.area,
          areaUnit: a.areaUnit,
          perimeter: a.perimeter,
          perimeterUnit: a.perimeterUnit,
          term: a.terms
        });
        // Simplified geometry may be LOOKED at, never saved: the model refuses save()/update()
        // while this flag is set, and the edit path swaps in the exact stored geometry first.
        annot._displayOnly = true;
        // the same three calls the viewer makes after any fetch (terms panel, tracks, links)
        await updateTermProperties(annot);
        await updateTrackProperties(annot);
        await updateAnnotationLinkProperties(annot);
        this.$eventBus.$emit('selectAnnotationInLayer', {annot, index: this.index});
      } catch (e) {
        // a failed pick must never break the click
      }
    },

    async viewMounted() {
      await this.$refs.view.$createPromise; // wait for ol.View to be created
      if (this.routedAnnotation) {
        this.centerViewOnAnnot(this.routedAnnotation, 500);
      }
      this.savePosition();
    },

    async setBaseSource() {
      await this.$refs.baseSource.$createPromise;
      this.baseSource = this.$refs.baseSource.$source;
    },

    async addOverviewMap() {
      if (!this.isPanelDisplayed('overview')) {
        return;
      }

      await this.$refs.map.$createPromise; // wait for ol.Map to be created
      await this.$refs.baseLayer.$createPromise; // wait for ol.Layer to be created

      let map = this.$refs.map.$map;

      this.overview = new OverviewMap({
        view: new View({projection: this.projectionName}),
        layers: [this.$refs.baseLayer.$layer],
        tipLabel: this.$t('overview'),
        target: this.$refs.overview,
        collapsed: this.imageWrapper.view.overviewCollapsed
      });
      map.addControl(this.overview);

      this.overview.getOverviewMap().on(('click'), (evt) => {
        let size = map.getSize();
        map.getView().centerOn(evt.coordinate, size, [size[0] / 2, size[1] / 2]);
      });
    },

    toggleOverview() {
      if (this.overview) {
        this.overview.setCollapsed(!this.imageWrapper.view.overviewCollapsed);
      }
    },

    togglePanel(panel) {
      this.$store.commit(this.imageModule + 'togglePanel', panel);
    },

    savePosition: _.debounce(async function () {
      if (this.$refs.view) {
        let extent = this.$refs.view.$view.calculateExtent(); // [minX, minY, maxX, maxY]

        // This re-arms itself every SAVE_POSITION_IN_IMAGE_INTERVAL, so an idle viewer used to
        // write the same rectangle forever (a Mongo insert plus an ACL check each time). Skip
        // the write when nothing moved; broadcasting keeps the heartbeat, since followers rely
        // on it.
        const broadcast = this.imageWrapper.tracking.broadcast;
        const positionKey = [extent.map(v => Math.round(v)).join(','), this.zoom, this.rotation].join('|');
        if (!broadcast && positionKey === this._lastPositionSaved) {
          clearTimeout(this.timeoutSavePosition);
          this.timeoutSavePosition = setTimeout(this.savePosition, constants.SAVE_POSITION_IN_IMAGE_INTERVAL);
          return;
        }
        try {
          await UserPosition.create({
            image: this.image.id,
            slice: this.slices[0].id,
            zoom: this.zoom,
            rotation: this.rotation,
            bottomLeftX: Math.round(extent[0]),
            bottomLeftY: Math.round(extent[1]),
            bottomRightX: Math.round(extent[2]),
            bottomRightY: Math.round(extent[1]),
            topLeftX: Math.round(extent[0]),
            topLeftY: Math.round(extent[3]),
            topRightX: Math.round(extent[2]),
            topRightY: Math.round(extent[3]),
            broadcast: broadcast
          });
          this._lastPositionSaved = positionKey;
        } catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-save-user-position')});
        }

        clearTimeout(this.timeoutSavePosition);
        this.timeoutSavePosition = setTimeout(this.savePosition, constants.SAVE_POSITION_IN_IMAGE_INTERVAL);
      }
    }, 500),

    fitZoom() {
      this.$refs.view.animate({
        zoom: this.idealZoom,
        center: [this.image.width / 2, this.image.height / 2]
      });
    },

    async centerViewOnAnnot(annot, duration) {
      if (annot.image === this.image.id) {
        if (!annot.location) {
          //in case annotation location has not been loaded
          annot = (await Cytomine.instance.api.get(`/annotations/${annot.id}`)).data;
          if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
            annot.location = decodeURIComponent(atob(annot.location));
          }
        }

        if (annot.project !== this.project.id) {
          await this.$router.push(`/project/${annot.project}/image/${annot.image}/annotation/${annot.id}`);
        }

        let geometry = this.format.readGeometry(annot.location);
        await this.$refs.view.fit(geometry, {duration, padding: [10, 10, 10, 10], maxZoom: this.image.zoom});

        if (!Object.prototype.hasOwnProperty.call(annot, 'centroid')) {
          return;
        }

        // HACK: center set by view.fit() is incorrect => reset it manually
        this.center = (geometry.getType() === 'Point') ? geometry.getFirstCoordinate()
          : [annot.centroid.x, annot.centroid.y];
        // ---
      }
    },

    async selectAnnotationHandler({index, annot, center = false, showComments = false}) {
      if (this.index === index && annot.image === this.image.id) {
        try {
          let sliceChange = false;
          if (!annot.slice) {
            //in case annotation slice has not been loaded
            annot = await Annotation.fetch(annot.id);
          }

          if (!this.sliceIds.includes(annot.slice)) {
            let slice = await SliceInstance.fetch(annot.slice);
            await this.$store.dispatch(this.imageModule + 'setActiveSlice', slice);
            this.$eventBus.$emit('reloadAnnotations', {idImage: this.image.id, hard: true});
            sliceChange = true;
          }

          if (showComments) {
            this.$store.commit(this.imageModule + 'setShowComments', annot);
          }

          this.selectedAnnotation = annot; // used to pre-load annot layer
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
          this.$eventBus.$emit('selectAnnotationInLayer', {index, annot});

          if (center) {
            await this.viewMounted();
            let duration = (sliceChange) ? undefined : 500;
            this.centerViewOnAnnot(annot, duration);
          }
        } catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
        }
      }
    },

    isPanelDisplayed(panel) {
      return this.configUI[`project-explore-${panel}`];
    },
    shortkeyHandler(key) {
      if (!key.startsWith('toggle-all-') && !this.isActiveImage) { // shortkey should only be applied to active map
        return;
      }

      key = key.replace('toggle-all-', 'toggle-');
      switch (key) {
        case 'toggle-information':
          if (this.isPanelDisplayed('info')) {
            this.togglePanel('info');
          }
          return;
        case 'toggle-zoom':
          if (this.isPanelDisplayed('digital-zoom')) {
            this.togglePanel('digital-zoom');
          }
          return;
        case 'toggle-link':
          if (this.isPanelDisplayed('link') && this.nbImages > 1) {
            this.togglePanel('link');
          }
          return;
        case 'toggle-filters':
          if (this.isPanelDisplayed('color-manipulation')) {
            this.togglePanel('colors');
          }
          return;
        case 'toggle-layers':
          if (this.isPanelDisplayed('image-layers')) {
            this.togglePanel('layers');
          }
          return;
        case 'toggle-ontology':
          if (this.isPanelDisplayed('ontology') && this.terms && this.terms.length > 0) {
            this.togglePanel('ontology');
          }
          return;
        case 'toggle-properties':
          if (this.isPanelDisplayed('property')) {
            this.togglePanel('properties');
          }
          return;
        case 'toggle-broadcast':
          if (this.isPanelDisplayed('follow')) {
            this.togglePanel('follow');
          }
          return;
        case 'toggle-review':
          if (this.isPanelDisplayed('review') && this.canEdit) {
            this.togglePanel('review');
          }
          return;
        case 'toggle-overview':
          if (this.isPanelDisplayed('overview')) {
            this.toggleOverview();
          }
          return;
      }
    },
    async takeScreenshot() {
      // Use of css percent values and html2canvas results in strange behavior
      // Set image container as actual height in pixel (not in percent) to avoid image distortion when retrieving canvas
      let containerHeight = document.querySelector('.map-container').clientHeight;
      document.querySelector('.map-container').style.height = containerHeight + 'px';

      let a = document.createElement('a');
      a.href = await this.$html2canvas(document.querySelector('.ol-unselectable'), {type: 'dataURL'});
      let imageName = 'image_' + this.image.id.toString() + '_project_' + this.image.project.toString() + '.png';
      a.download = imageName;
      a.click();

      // Reset container css values as previous
      document.querySelector('.map-container').style.height = '';
    },
  },
  async created() {
    if (!getProj(this.projectionName)) { // if image opened for the first time
      let projection = createProj({code: this.projectionName, units: 'pixels', extent: this.extent});
      addProj(projection);
    }

    if (this.routedAction === 'review') {
      this.togglePanel('review');
      if (!this.image.inReview) {
        try {
          let clone = await this.image.clone().review();
          this.$store.commit(this.imageModule + 'setImageInstance', clone);
        } catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-start-review')});
        }
      }
      this.$store.commit(this.imageModule + 'setReviewMode', true);
    }

    // remove all selected features in order to reselect them when they will be added to the map (otherwise,
    // issue with the select interaction)
    this.selectedLayers.forEach(layer => {
      this.$store.commit(this.imageModule + 'removeLayerFromSelectedFeatures', {layer, cache: true});
    });

    let annot = this.imageWrapper.routedAnnotation;
    if (!annot) {
      let idRoutedAnnot = this.$route.params.idAnnotation;
      if (idRoutedAnnot) {
        try {
          annot = (await Cytomine.instance.api.get(`/annotations/${idRoutedAnnot}`)).data;
          if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
            annot.location = decodeURIComponent(atob(annot.location));
          }
        } catch (error) {
          console.log(error);
          this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
        }
      }
    }

    if (annot) {
      if (Object.prototype.hasOwnProperty.call(annot, 'annotationLayer')) {
        let response = await Cytomine.instance.api.get(`/annotation-layers/${annot.annotationLayer}/task-run-layer`);
        let taskRunLayer = response.data;
        annot.image = taskRunLayer.image;
      }

      try {
        if (annot.image === this.image.id) {
          if (!this.sliceIds.includes(annot.slice) && Object.prototype.hasOwnProperty.call(annot, 'slice')) {
            let slice = await SliceInstance.fetch(annot.slice);
            await this.$store.dispatch(this.imageModule + 'setActiveSlice', slice);
          }
          this.routedAnnotation = annot;
          if (this.routedAction === 'comments') {
            this.$store.commit(this.imageModule + 'setShowComments', annot);
          }
          this.$store.commit(this.imageModule + 'setAnnotToSelect', annot);
        }

        this.$store.commit(this.imageModule + 'clearRoutedAnnotation');
      } catch (error) {
        console.log(error);
        this.$notify({type: 'error', text: this.$t('notif-error-target-annotation')});
      }
    }

    try {
      await new ImageConsultation({image: this.image.id}).save();
    } catch (error) {
      console.log(error);
      this.$notify({type: 'error', text: this.$t('notif-error-save-image-consultation')});
    }

    this.loading = false;
  },
  mounted() {
    this.$eventBus.$on('addAnnotation', this.onAnnotationWrite);
    this.$eventBus.$on('editAnnotation', this.onAnnotationWrite);
    this.$eventBus.$on('deleteAnnotation', this.onAnnotationWrite);
    this.$eventBus.$on('annotationPlane', this.onAnnotationPlane);
    this.$eventBus.$on('reloadAnnotations', this.onReloadAnnotations);
    this.$eventBus.$on('annotationLoadingStart', this.onAnnotationLoadingStart);
    this.$eventBus.$on('annotationLoadingEnd', this.onAnnotationLoadingEnd);
    this.$eventBus.$on('updateMapSize', this.updateMapSize);
    this.$eventBus.$on('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$on('selectAnnotation', this.selectAnnotationHandler);
    this.$eventBus.$on('close-metadata', () => this.$store.commit(this.imageModule + 'togglePanel', 'info'));
    this.setInitialZoom();
  },
  beforeDestroy() {
    clearTimeout(this._annWriteTimer);
    clearTimeout(this.tileErrorTimer);
    this.$eventBus.$off('addAnnotation', this.onAnnotationWrite);
    this.$eventBus.$off('editAnnotation', this.onAnnotationWrite);
    this.$eventBus.$off('deleteAnnotation', this.onAnnotationWrite);
    this.$eventBus.$off('annotationPlane', this.onAnnotationPlane);
    this.$eventBus.$off('reloadAnnotations', this.onReloadAnnotations);
    this.$eventBus.$off('annotationLoadingStart', this.onAnnotationLoadingStart);
    this.$eventBus.$off('annotationLoadingEnd', this.onAnnotationLoadingEnd);
    clearTimeout(this.tilesHideTimer);
    clearTimeout(this.annotsHideTimer);
    this.clearAnnotationRaster();
    this.$eventBus.$off('updateMapSize', this.updateMapSize);
    this.$eventBus.$off('shortkeyEvent', this.shortkeyHandler);
    this.$eventBus.$off('selectAnnotation', this.selectAnnotationHandler);
    this.$eventBus.$off('close-metadata');
    clearTimeout(this.timeoutSavePosition);
  }
};
</script>

<style lang="scss">
@import '~vuelayers/lib/style.css';

$backgroundPanelBar: #555;
$widthPanelBar: 2.8rem;
$backgroundPanel: #f2f2f2;
$colorPanelLink: #eee;
$colorHoverPanelLink: white;
$colorBorderPanelLink: #222;
$colorOpenedPanelLink: #6c95c8;

.map-container {
  display:flex;
  position: relative;
  width: 100%;
  height: 100%;
}

.map {
  flex-grow: 1;
}

.draw-tools {
  position: absolute;
  top: 0.7em;
  left: 3.5rem;
  right: $widthPanelBar;
  z-index: 30;
}

.annotations-unavailable-notice {
  position: absolute;
  top: 6.4rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 60;
  display: flex;
  align-items: center;
  gap: 0.5em;
  padding: 0.35em 0.8em;
  border-radius: 4px;
  background: rgba(181, 88, 12, 0.95);
  color: white;
  font-size: 0.8rem;
  font-weight: 600;
  pointer-events: none;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.25);
}

.broadcast {
  position: absolute;
  right: 4.5rem;
  top: 0.7em;
  text-transform: uppercase;
  font-weight: 600;
  background-color: #EE4242;
  color: white;
  padding: 0.25em 0.75em 0.25em 0.55em;
  border-radius: 5px;
  border: 2px solid white;

  i.fas {
    margin-right: 0.3em;
  }
}

.panels {
  background: $backgroundPanelBar;
  display: flex;
  font-size: 0.9em;

  > ul {
    padding: 0;
    margin: 0;

    > li {
      position: relative;

      > a {
        position: relative;
        display: block;
        width: $widthPanelBar;
        padding: 0.35rem 0.8rem;
        font-size: 1.25rem;
        color: $colorPanelLink;
        border-bottom: 1px solid $colorBorderPanelLink;
        text-decoration: none;
        text-align:center;

        &:hover {
          color: $colorHoverPanelLink;
        }

        &.active {
          background: $backgroundPanel;
          color: $colorOpenedPanelLink;
        }

        &.close {
          color: #ffc4c4;
          :hover {
            color: #ff7070;
          }
        }
      }
    }
  }
}

.panel-options {
  position: absolute;
  bottom: -1.75em;
  right: $widthPanelBar;
  width: 24em;
  min-height: 10em;
  background: $backgroundPanel;
  padding: 0.75em;
  border-radius: 5px 0 0 5px;
  z-index: 100;

  h1 {
    font-size: 1.1rem;
    padding-top: 0.3rem !important;
    padding-bottom: 1rem !important;
  }

  table {
    background: none;
    width: 100%;
  }
}

.panels li:nth-child(-n+8) .panel-options {
  bottom: -7.5em;
  min-height: 13em;
}

.panels li:nth-child(-n+4) .panel-options {
  top: -1.75em;
  bottom: auto;
  min-height: 7.5em;
}

.panels li:nth-child(5) .panel-options {
  top: -5.5em;
  bottom: auto;
}

/* ----- Metadata panel ---- */

.panel-metadata {
  position: absolute;
  top: -1.5em;
  right: $widthPanelBar;
  min-width: 30em;
  min-height: 30em;
  background: $backgroundPanel;
  padding: 0.75em;
  border-radius: 5px 0 0 5px;
}

/* ----- CUSTOM STYLE FOR OL CONTROLS ----- */

.ol-zoom, .ol-rotate {
  background: none !important;
  z-index: 20;
}

.ol-rotate:not(.custom) {
  display: none;
}

.ol-control button {
  background: white !important;
  color: black !important;
  border-radius: 2px !important;
  box-shadow: 0 0 1px #777;

  &:hover {
    box-shadow: 0 0 1px black;
    cursor: pointer;
  }
}

.ol-zoom-in {
  margin-bottom: 5px !important;
}

/* ----- Rotation selector ----- */
.rotation-selector-wrapper {
  position: absolute;
  left: .5em;
  top: 5rem;
  z-index: 20;
}

/* ----- Vertical layers-opacity control (under the rotation compass) ----- */
.layers-opacity-fly {
  position: absolute;
  left: .5em;
  top: calc(5rem + 40px);
  width: 30px;
  height: 110px;
  background: white;
  border: 1px solid #e5e8ee;
  border-radius: 6px;
  box-shadow: 0 0 1px #777;
  z-index: 20;
  display: flex;
  align-items: center;
  justify-content: center;
}

.layers-opacity-fly .lof-slider {
  width: 92px;                 /* becomes the vertical travel once rotated */
  transform: rotate(-90deg);   /* max at the TOP: slide up = more opaque */
  margin: 0;
  cursor: pointer;
  accent-color: #1e63d0;
}

/* The expanded rotation panel occupies the same corner: hide the control meanwhile. */
.rotation-selector-wrapper.expanded + .layers-opacity-fly {
  display: none;
}

.custom-overview {
  position: absolute;
  bottom: 0.5em;
  left: 0.5em;
  background: rgba(255, 255, 255, 0.8);
  display: flex;
  flex-direction: column;
  border-radius: 4px;

  .ol-overviewmap {
    position: static;
    background: none;
  }

  .ol-overviewmap:not(.ol-collapsed) button {
    bottom: 2px !important;
  }

  .image-name {
    font-size: 0.8em;
    padding: 2px 5px;
    width: 158px;
    word-wrap: break-word;

    &.hidden {
      display: none;
    }
  }
}

/* ----- Image controls ----- */
.image-controls-wrapper {
  position: absolute;
  bottom: 1.5rem;
  left: 20%;
  right: calc(#{$widthPanelBar} + 20%);
  z-index: 5;
}

/* ----- Annotation list ----- */
.annotations-table-wrapper {
  position: absolute;
  bottom: 0;
  left: 0;
  right: $widthPanelBar;
  z-index: 40;
  pointer-events: none;
}
</style>
